import { Avatar, Button, Card, CardActions, CardContent, Typography, CardHeader, Divider, Dialog, DialogContent, Accordion, AccordionDetails, AccordionSummary, List, ListItem, ListItemText, Tooltip, Alert, Box, LinearProgress, IconButton } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Order, OrderState } from "../../../../app/models/order";
import { useState } from "react";
import ClientOrderScanUploadForm from "../ClientOrderScanUploadForm";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { currencyFormat, formatToLocalDate, formatToLocalTime, getOrderStatusColor, getOrderStatusTitle, getPolishLiteral } from "../../../../app/util/util";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import agent from "../../../../app/api/agent";
import { LoadingButton } from "@mui/lab";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { toast } from "react-toastify";

interface Props {
    order: Order;
    handleCardSelectOrder?: (handledOrder: Order) => void;
}

export default function ClientOrderCard({ order, handleCardSelectOrder }: Props) {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const benefitients = order.benefitients ? order.benefitients.split(';').map(item => {
        const [lastName, firstName, pesel, birthPlace, birthDate, certId] = item.split('~');
        return {
            lastName, firstName, pesel, birthPlace, birthDate,
            certId: certId !== undefined ? certId : ''
        };
    }) : [];
    const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
    const [isGeneratingTest, setIsGeneratingTest] = useState(false);

    function getStatusIcon(currentState: number) {
        switch (currentState) {
            case 0:
                return <HourglassTopIcon />;
            case 1:
                return <HourglassTopIcon />;
            case 2:
                return <DoneIcon />;
            case 3:
                return <DoneIcon />;
            case 4:
                return <HelpOutlineIcon />;
            case 5:
                return <CancelIcon />;
            case 6:
                return <HourglassTopIcon />;
            case 7:
                return <DoneIcon />;
            default:
                return <ErrorIcon />;
        }
    }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const downloadBlob = (blob: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
    }

    async function handleGenerateCertificates(orderId: number) {
        // Dodatkowe sprawdzenie czy zamówienie nie jest anulowane
        if (order.orderState === 5) {
            toast.error('Nie można pobrać certyfikatów dla anulowanego zamówienia');
            return;
        }
        
        setIsGeneratingCertificate(true);
        try {
            const response = await agent.GenerateCertificate.generateCertificate(orderId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            downloadBlob(blob, `Certyfikaty_${orderId}.pdf`);
            toast.success('Certyfikaty zostały pomyślnie pobrane');
        } catch (error) {
            console.error("Błąd podczas generowania certyfikatów", error);
            toast.error('Nie udało się pobrać certyfikatów');
        }
        setIsGeneratingCertificate(false);
    }

    const handleGenerateTestPdf = async () => {
        if (!order) return;
        
        // Dodatkowe sprawdzenie czy zamówienie nie jest anulowane
        if (order.orderState === 5) {
            toast.error('Nie można pobrać testu dla anulowanego zamówienia');
            return;
        }

        setIsGeneratingTest(true);
        try {
            await agent.Client.Orders.generateTestPdf(order.publicId);
            toast.success('Test został pomyślnie pobrany');
        } catch (error) {
            console.error('Błąd podczas pobierania testu PDF:', error);
            toast.error('Nie udało się pobrać testu');
        } finally {
            setIsGeneratingTest(false);
        }
    };

    const handleCopyOrder = () => {
        navigate('/client/orders', { 
            state: { copyFrom: order } 
        });
        toast.info(`Wypełniono formularz danymi z zamówienia #${order.publicId}. Uzupełnij datę realizacji i zapisz.`);
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <Tooltip title="Utwórz nowe zamówienie na podstawie tego">
                <IconButton
                    aria-label="copy order"
                    onClick={handleCopyOrder}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        width: 36,
                        height: 36,
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'primary.dark',
                        }
                    }}
                    size="small"
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: getOrderStatusColor(order.orderState) }} title={getOrderStatusTitle(order.orderState)}> {getStatusIcon(order.orderState)}</Avatar>
                }
                title={order.userDisplayName && order.userDisplayName.trim() ? order.userDisplayName : order.userEmail}
                titleTypographyProps={{
                    sx: { fontWeight: 'bold', color: 'secondary' }
                }}
                subheader={order.orderDateTime ? `${formatToLocalDate(order.orderDateTime.toString())} ${formatToLocalTime(order.orderDateTime.toString())}` : 'Brak daty'}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2">
                    <strong>{order.customeClientServiceName}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    nazwa wskazana przez Klienta
                </Typography>
                <Typography variant="subtitle2">
                    <strong>#{order.publicId}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    numer zamówienia
                </Typography>
                <Typography variant="subtitle2" sx={{ color: getOrderStatusColor(order.orderState) }}>
                    <strong>{getOrderStatusTitle(order.orderState)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    status zamówienia
                </Typography>
                <Typography>
                    <strong>{currencyFormat(order.totalDiscountedPrice)}</strong> ( {currencyFormat(order.unitDiscountedPrice)} x {getPolishLiteral(order.totalItem)} )
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    wartość zamówienia
                </Typography>

                {order.clientComment &&
                    <>
                        <Typography variant="subtitle2">
                            <strong>{order.clientComment}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                            komentarz/uwagi Klienta
                        </Typography>
                    </>
                }

                <Typography>
                    {order.userPhone}
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    nr telefonu podany przez Klienta
                </Typography>
            </CardContent>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Typography>Uczestnicy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <List>
                        {benefitients.map((benefitient, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`${benefitient.firstName} ${benefitient.lastName}`} secondary={`Pesel: ${benefitient.pesel}, Miejsce urodzenia: ${benefitient.birthPlace}, Data urodzenia: ${benefitient.birthDate}, Certyfikat: ${benefitient.certId}`} />
                            </ListItem>
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
            <CardActions
                disableSpacing
                sx={{
                    mt: "auto",
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Button
                    sx={{ width: '100px', height: '40px' }}
                    variant='outlined'
                    component={Link} to={`/client/orders/${order.publicId}`}
                    size="small"
                    title='Więcej'
                >
                    Więcej
                </Button>

                {(order.orderState === 0 || order.orderState === 1) && (
                    <Button
                        sx={{ width: '100px', height: '40px' }}
                        variant='outlined'
                        onClick={() => handleCardSelectOrder!(order)}
                        size="small"
                        title='Edytuj'
                        color='warning'
                        disabled={order.isTestGenerationInProgress}
                    >
                        Edytuj
                    </Button>
                )}

                {(order.orderState === 0 || order.orderState === 1 || order.orderState === 2) && (
                    <Button
                        sx={{ width: '100px', height: '40px' }}
                        variant='outlined'
                        onClick={handleOpen}
                        size="small"
                        title='Załącz'
                        color='info'
                        disabled={order.isTestGenerationInProgress}
                    >
                        Załącz
                    </Button>
                )}

                {/* Przycisk dodawania skanów dla zamówień zatwierdzonych bez skanów lub po dodaniu skanów po zatwierdzeniu */}
                {(order.orderState === OrderState.APPROVED_WITHOUT_SCANS || order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL) && (
                    <Button
                        sx={{ height: '40px' }}
                        variant='contained'
                        onClick={handleOpen}
                        size="small"
                        title='Dodaj skany'
                        color='warning'
                        disabled={order.isTestGenerationInProgress}
                    >
                        Dodaj skany
                    </Button>
                )}

                {/* Zmodyfikowany przycisk Pobierz test */}
                <Tooltip title={
                    order.isTestGenerationInProgress
                        ? 'Zamówienie jest przetwarzane'
                        : order.orderState === 5 
                            ? 'Zamówienie anulowane - test niedostępny'
                            : order.isTestAvailable 
                                ? 'Pobierz test' 
                                : 'Test nie jest jeszcze dostępny'
                }>
                    <span>
                        <LoadingButton
                            loading={isGeneratingTest}
                            sx={{ height: '40px' }}
                            variant='outlined'
                            onClick={handleGenerateTestPdf}
                            size="small"
                            title='Pobierz test'
                            color='primary'
                            startIcon={<CloudDownloadIcon />}
                            disabled={order.isTestGenerationInProgress || !order.isTestAvailable || order.orderState === 5}
                        >
                            Test
                        </LoadingButton>
                    </span>
                </Tooltip>

                {/* Zmodyfikowany przycisk do pobierania certyfikatów */}
                {order.isExamApproved && (
                    <Tooltip title={
                        order.isTestGenerationInProgress
                            ? 'Zamówienie jest przetwarzane'
                            : order.orderState === 5 
                                ? 'Zamówienie anulowane - certyfikaty niedostępne' 
                                : 'Pobierz certyfikaty'
                    }>
                        <span>
                            <LoadingButton
                                loading={isGeneratingCertificate}
                                sx={{ height: '40px' }}
                                variant='outlined'
                                onClick={() => handleGenerateCertificates(order.publicId)}
                                size="small"
                                title='Certyfikaty'
                                color='success'
                                disabled={order.isTestGenerationInProgress || order.orderState === 5}
                            >
                                Pobierz Certyfikaty
                            </LoadingButton>
                        </span>
                    </Tooltip>
                )}

                <Dialog open={open} onClose={handleClose}>
                    <DialogContent>
                        <ClientOrderScanUploadForm order={order} cancelUpload={handleClose} />
                    </DialogContent>
                </Dialog>
            </CardActions>

            {/* Informacja o weryfikacji zamówienia */}
            {order.isTestGenerationInProgress && (
                <Box sx={{ px: 2, pb: 2 }}>
                    <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Zamówienie jest weryfikowane
                        </Typography>
                        <Typography variant="caption">
                            Proces potrwa około minuty. Strona odświeży się automatycznie. Po tym czasie będziesz mógł zarządzać zamówieniem.
                        </Typography>
                        <LinearProgress sx={{ mt: 1 }} />
                    </Alert>
                </Box>
            )}
        </Card>
    )
}